import { useState, useCallback, useRef, useEffect } from 'react';
import {
  downloadFilesAsZip,
  type DownloadableFile,
  type DownloadProgress,
  type DownloadResult,
} from 'src/lib/utils/download-utils';
import { multiDownload } from 'src/lib/utils/multi-download';
import { useToast } from 'src/components/ui/use-toast';
import { useTranslation } from 'react-i18next';

export interface UseDownloadFilesOptions {
  /** Function to collect files to download */
  getFiles: () => DownloadableFile[];
  /** Name for the ZIP file (without .zip extension) */
  zipFileName?: string;
}

export const useDownloadFiles = ({ getFiles, zipFileName = 'files' }: UseDownloadFilesOptions) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState<DownloadProgress | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const handleDownloadAsZip = useCallback(async () => {
    setIsDownloading(true);
    setDownloadProgress(null);

    // Create new AbortController for this download
    abortControllerRef.current = new AbortController();

    try {
      const files = getFiles();

      if (files.length === 0) {
        toast({
          variant: 'destructive',
          description: t('actions:no_files_description', {
            defaultValue: 'No downloadable files found.',
          }),
        });
        return;
      }

      const result = await downloadFilesAsZip(
        files,
        `${zipFileName}.zip`,
        (progress: DownloadProgress) => {
          setDownloadProgress(progress);
        },
        abortControllerRef.current.signal,
      );

      // Show appropriate toast based on result
      if (result.failedCount === 0) {
        toast({
          variant: 'success',
          description: t('actions:client_download_success', {
            defaultValue: `Successfully downloaded ${files.length} files.`,
          }),
        });
      } else if (result.successCount > 0) {
        toast({
          variant: 'warning',
          description: t('actions:client_download_partial', {
            defaultValue: `Downloaded ${result.successCount}/${files.length} files. ${
              result.failedCount
            } files failed: ${result.failedFiles.join(', ')}`,
          }),
        });
      }
    } catch (error) {
      // Handle cancellation gracefully
      if (error instanceof Error && error.name === 'AbortError') {
        toast({
          variant: 'info',
          description: t('actions:download_cancelled', {
            defaultValue: 'Download cancelled.',
          }),
        });
        return;
      }

      console.error('Download failed:', error);
      toast({
        variant: 'destructive',
        description: t('actions:download_error_description', {
          defaultValue: 'Failed to download files. Please try again.',
        }),
      });
    } finally {
      setIsDownloading(false);
      setDownloadProgress(null);
      abortControllerRef.current = null;
    }
  }, [getFiles, zipFileName, toast, t]);

  // Auto-cancel downloads when component unmounts (user navigates away)
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const handleDownloadOneByOne = useCallback(async () => {
    const files = getFiles();

    if (files.length === 0) {
      toast({
        variant: 'destructive',
        description: t('actions:no_files_description', {
          defaultValue: 'No downloadable files found.',
        }),
      });
      return;
    }

    setIsDownloading(true);

    try {
      const result = await multiDownload(files.map((f) => ({ url: f.url, filename: f.filename })));

      if (result.failedFiles.length === 0) {
        toast({
          variant: 'success',
          description: t('actions:download_started', {
            defaultValue: `Started downloading ${result.successCount} files.`,
          }),
        });
      } else if (result.successCount > 0) {
        toast({
          variant: 'destructive',
          title: t('actions:download_partial_title', {
            defaultValue: 'Some files could not be downloaded',
          })!,
          description: t('actions:download_partial_description', {
            defaultValue: `${result.failedFiles.length} file(s) may have been removed or are no longer available: ${result.failedFiles.join(', ')}`,
          }),
        });
      } else {
        toast({
          variant: 'destructive',
          title: t('actions:download_all_failed_title', {
            defaultValue: 'Download failed',
          })!,
          description: t('actions:download_all_failed_description', {
            defaultValue: 'Files may have been removed or are no longer available. Please contact support if this issue persists.',
          }),
        });
      }
    } catch (error) {
      console.error('Download one-by-one failed:', error);
      toast({
        variant: 'destructive',
        description: t('actions:download_error_description', {
          defaultValue: 'Failed to download files. Please try again.',
        }),
      });
    } finally {
      setIsDownloading(false);
    }
  }, [getFiles, toast, t]);

  return {
    isDownloading,
    downloadProgress,
    handleDownloadAsZip,
    handleDownloadOneByOne,
  };
};
